/**
 * Experience Configuration
 * Centralized configuration for each AR experience
 * Supports multiple client experiences with different CTAs, themes, and content
 */

export interface ExperienceConfig {
  experienceId: string
  clientName: string
  ctaText: string
  ctaUrl: string
  ctaIcon?: string
  triggerAssets: string[]
  themeAccent: string
  aboutText: string
  aboutTitle?: string
  rockablockLink?: string
  airtableLink?: string
}

/**
 * Default ROQ experience configuration
 */
export const ROQ_EXPERIENCE_CONFIG: ExperienceConfig = {
  experienceId: 'roq-ar-demo',
  clientName: 'ROQ',
  // Matches brief format: "✨ ROQ — Learn More →"
  ctaText: '✨ ROQ — Learn More →',
  ctaUrl: 'https://www.roq.us/',
  ctaIcon: '✨',
  triggerAssets: ['/assets/compiled-image-targets/roq-target.mind'],
  themeAccent: '#ff086d', // Pink accent from brand
  aboutText: `ROCK AR is an innovative WebAR platform that brings augmented reality experiences directly to your browser. No app downloads required—just point your camera and experience immersive AR content powered by cutting-edge technology.

This experience is powered by ROCKaBLOCK, a creative technology studio specializing in premium digital experiences. ROCKaBLOCK combines technical excellence with creative vision to deliver memorable brand interactions.`,
  aboutTitle: 'About ROCK AR',
  rockablockLink: 'https://rockablock.com',
  airtableLink: 'https://airtable.com/appDmQ9zyjic5DJOr/shrKiuntfBe4lFCeU'
}

/**
 * Get current experience configuration
 * Now supports multi-tenant routing based on URL path
 */
export function getCurrentExperienceConfig(): ExperienceConfig {
  // Import client config synchronously
  // Note: In browser ES modules, we need to use a synchronous approach
  // This function is called at module load time, so we use a try-catch with require
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { getClientIdFromURL, getClientConfig } = require('./clients.js')
    const clientId = getClientIdFromURL()
    
    if (clientId) {
      const clientConfig = getClientConfig(clientId)
      if (clientConfig) {
        return clientConfig.experienceConfig
      }
    }
  } catch (error) {
    // If require fails (e.g., in browser with ES modules), fall back to default
    // This is expected in browser environments where require is not available
    // The client config will be loaded via ES module imports elsewhere
    console.warn('Could not load client config synchronously, using default:', error)
  }
  
  // Fallback to default ROQ experience for root path or invalid client
  return ROQ_EXPERIENCE_CONFIG
}
